/**
 * Recording a voice note in the browser (decision 2026-09-23 #22).
 *
 * The recording is sent as the browser made it: Chrome and Android make WebM with Opus, Safari makes MP4
 * with AAC, and nothing is converted. The browser APIs are passed in, so the rules — the length limit, the
 * too-short press, what a refused microphone means — can be tested without a microphone.
 */

/** A press shorter than this is a tap, not a message, and is thrown away. */
export const VOICE_NOTE_MIN_MS = 700;

/** Preferred formats, in order. The first one the browser can record is used. */
const PREFERRED_TYPES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus'];

/** Why a recording could not start. Each has its own words on screen, because each has its own remedy. */
export type RecorderProblem = 'unsupported' | 'denied' | 'noMicrophone' | 'failed';

export class RecorderError extends Error {
  constructor(readonly problem: RecorderProblem) {
    super(`Voice recording could not start: ${problem}`);
    this.name = 'RecorderError';
  }
}

/** The part of MediaRecorder this module uses, so a test can stand one in. */
export interface RecorderLike {
  readonly state: 'inactive' | 'recording' | 'paused';
  readonly mimeType: string;
  /** Called once, before `start`: each piece of audio as it arrives, and the end of the recording. */
  listen(onData: (data: Blob) => void, onStop: () => void): void;
  start(): void;
  stop(): void;
}

export interface RecorderEnvironment {
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  createRecorder?: (stream: MediaStream, mimeType: string | undefined) => RecorderLike;
  isTypeSupported?: (mimeType: string) => boolean;
  now: () => number;
  setTimeout: (callback: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (handle: ReturnType<typeof setTimeout>) => void;
}

/** The real browser, or as much of it as exists: an older browser simply lacks the recorder. */
export function browserEnvironment(): RecorderEnvironment {
  const devices = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  const Recorder = typeof MediaRecorder !== 'undefined' ? MediaRecorder : undefined;

  return {
    getUserMedia: devices?.getUserMedia ? (constraints) => devices.getUserMedia(constraints) : undefined,
    createRecorder: Recorder
      ? (stream, mimeType) => {
        const recorder = new Recorder(stream, mimeType ? { mimeType } : undefined);
        return {
          get state() { return recorder.state; },
          get mimeType() { return recorder.mimeType; },
          listen: (onData, onStop) => {
            recorder.ondataavailable = (event) => onData(event.data);
            recorder.onstop = () => onStop();
          },
          start: () => recorder.start(),
          stop: () => recorder.stop(),
        };
      }
      : undefined,
    isTypeSupported: Recorder ? (type) => Recorder.isTypeSupported(type) : undefined,
    now: () => performance.now(),
    setTimeout: (callback, ms) => setTimeout(callback, ms),
    clearTimeout: (handle) => clearTimeout(handle),
  };
}

export interface Recording {
  blob: Blob;
  durationMs: number;
  /** True when it stopped at the length limit rather than when the person let go. */
  reachedLimit: boolean;
}

export interface VoiceRecording {
  /** Stops and keeps it. Resolves with what was recorded, or null for a press too short to be a message. */
  stop(): Promise<Recording | null>;
  /** Stops and throws it away. */
  cancel(): void;
  /** Settles once, however the recording ends: let go, cancelled, or stopped at the limit. */
  readonly finished: Promise<Recording | null>;
}

/**
 * Asks for the microphone and starts recording.
 *
 * The first call on a site shows the browser's permission prompt, so this can take as long as the person
 * takes to answer it. A refusal is a {@link RecorderError} naming what went wrong.
 *
 * @param maxMs Where the recording stops itself: `chat.voiceNoteMaximumMs` from `useLimits`, the length the
 *   server refuses anything over.
 */
export async function startVoiceRecording(
  maxMs: number,
  env: RecorderEnvironment = browserEnvironment(),
): Promise<VoiceRecording> {
  if (!env.getUserMedia || !env.createRecorder) throw new RecorderError('unsupported');

  let stream: MediaStream;
  try {
    stream = await env.getUserMedia({ audio: true });
  } catch (error) {
    throw new RecorderError(problemOf(error));
  }

  const mimeType = PREFERRED_TYPES.find((type) => env.isTypeSupported?.(type) ?? false);
  const recorder = env.createRecorder(stream, mimeType);
  const chunks: Blob[] = [];
  const startedAt = env.now();
  let stoppedAt = startedAt;
  let cancelled = false;
  let reachedLimit = false;

  const finished = new Promise<Recording | null>((resolve) => {
    recorder.listen((data) => {
      if (data.size > 0) chunks.push(data);
    }, () => {
      // The microphone light goes off as soon as the recording ends, not when the page is closed.
      stream.getTracks().forEach((track) => track.stop());

      const durationMs = Math.min(stoppedAt - startedAt, maxMs);
      if (cancelled || durationMs < VOICE_NOTE_MIN_MS) {
        resolve(null);
        return;
      }

      resolve({
        blob: new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' }),
        durationMs,
        reachedLimit,
      });
    });
  });

  const halt = () => {
    if (recorder.state === 'inactive') return;
    stoppedAt = env.now();
    env.clearTimeout(limit);
    recorder.stop();
  };

  const limit = env.setTimeout(() => {
    reachedLimit = true;
    halt();
  }, maxMs);

  recorder.start();

  return {
    finished,
    stop: () => {
      halt();
      return finished;
    },
    cancel: () => {
      cancelled = true;
      halt();
    },
  };
}

/** What a failed `getUserMedia` means for the person holding the phone. */
function problemOf(error: unknown): RecorderProblem {
  const name = error instanceof Error || error instanceof DOMException ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'noMicrophone';
  return 'failed';
}

/** Whether this browser can play a recording of this type. Where it cannot, a download link is offered. */
export function canPlayAudio(contentType: string | null | undefined): boolean {
  if (!contentType || typeof document === 'undefined') return false;
  return document.createElement('audio').canPlayType(contentType) !== '';
}
